import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Transaction } from '../../../services/types';
import { getAzPhoneNumberMask, getCardNumberMask, getPhoneNumberMask } from '../../../services/utils';
import m10Logo from '../../../assets/images/m10.svg';
import {
  LionScreen,
  LionLeaveModal,
  LionLowTimeModal,
  LION_GREEN_LIGHT,
  LION_GREY,
} from '../components/LionLayout';
import LionAmountAlert from '../components/LionAmountAlert';
import { showsAmountChangeNotice } from '../../../services/amountChange';
import { BankLogo } from '../../../utils/bankLogos';
import { copyToClipboard } from '../../../services/clipboard';
import QrPayBlock from '../../../components/organisms/QrPayBlock/QrPayBlock';
import type { CancelReasonOption, DeclineContext } from '../../../services/types';

// ─── QR SVG icon ──────────────────────────────────────────────────────────────
const QrIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ height: 24, flexShrink: 0 }}>
    <rect x="2" y="2" width="8" height="8" rx="1.5" stroke="#333" strokeWidth="1.5"/>
    <rect x="14" y="2" width="8" height="8" rx="1.5" stroke="#333" strokeWidth="1.5"/>
    <rect x="2" y="14" width="8" height="8" rx="1.5" stroke="#333" strokeWidth="1.5"/>
    <rect x="5" y="5" width="2" height="2" fill="#333"/>
    <rect x="17" y="5" width="2" height="2" fill="#333"/>
    <rect x="5" y="17" width="2" height="2" fill="#333"/>
    <rect x="14" y="14" width="3" height="3" fill="#333"/>
    <rect x="19" y="19" width="3" height="3" fill="#333"/>
  </svg>
);

// ─── СБП SVG logo ─────────────────────────────────────────────────────────────
const SbpIcon = () => (
  <svg width="100" height="50" viewBox="0 0 100 50" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ height: 30, flexShrink: 0 }}>
    <path d="M0 10.8838L6.05499 21.7067V28.3084L0.00708333 39.11L0 10.8838Z" fill="#5B57A2"/>
    <path d="M23.2487 17.7676L28.9224 14.2901L40.5341 14.2793L23.2487 24.8685V17.7676Z" fill="#D90751"/>
    <path d="M23.2166 10.8196L23.2487 25.1487L17.1795 21.4196V0L23.217 10.8196H23.2166Z" fill="#FAB718"/>
    <path d="M40.5341 14.2796L28.922 14.2904L23.2166 10.8196L17.1795 0L40.5337 14.2796H40.5341Z" fill="#ED6F26"/>
    <path d="M23.2487 39.17V32.2179L17.1795 28.5596L17.1829 50.0004L23.2487 39.17Z" fill="#63B22F"/>
    <path d="M28.9083 35.7242L6.05458 21.7067L0 10.8838L40.5095 35.71L28.9079 35.7242H28.9083Z" fill="#1487C9"/>
    <path d="M17.1834 50.0004L23.2484 39.17L28.908 35.7241L40.5092 35.71L17.1834 50.0004Z" fill="#017F36"/>
    <path d="M0.0071106 39.1093L17.2292 28.5593L11.4392 25.0068L6.05502 28.3077L0.0071106 39.1093Z" fill="#984995"/>
    <path fillRule="evenodd" clipRule="evenodd" d="M66.2021 26.6991C70.1096 26.6991 73.0112 24.3033 73.0112 20.6713C73.0112 17.1557 70.8705 14.8734 67.2924 14.8734C65.6412 14.8734 64.2787 15.4544 63.2525 16.4574C63.4974 14.3847 65.2504 12.871 67.1803 12.871C67.6259 12.871 70.9791 12.8641 70.9791 12.8641L72.8755 9.23535C72.8755 9.23535 68.6655 9.33138 66.7083 9.33138C62.2367 9.4091 59.2162 13.4736 59.2162 18.4101C59.2162 24.1617 62.162 26.6991 66.2021 26.6991ZM66.2259 18.0875C67.6767 18.0875 68.6824 19.0408 68.6824 20.6709C68.6824 22.1378 67.7888 23.3467 66.2259 23.3499C64.7308 23.3499 63.7249 22.2297 63.7249 20.6957C63.7249 19.0653 64.7308 18.0875 66.2259 18.0875ZM88.0995 14.038V26.2621H83.7344V17.6871H79.5316V26.2621H75.1665V14.0372L88.0995 14.038ZM53.0928 22.8763C54.6325 22.764 55.6632 22.1695 55.6632 22.1695L57.2786 25.1582C56.244 25.8227 54.5466 26.2548 53.3162 26.3004C48.9141 26.3813 45.852 23.7027 45.852 19.8001C45.852 15.9468 49.1098 13.2043 53.3162 13.2043C54.9828 13.2043 56.4157 13.6514 57.4916 14.4669C57.2651 14.8681 56.9573 15.4239 56.6648 15.9557L56.1962 16.8094L55.77 17.589C55.77 17.589 54.2199 16.7768 53.0482 16.7768C51.1374 16.7768 49.7458 17.8873 49.7458 19.8245C49.7458 21.811 51.3228 22.9288 53.0928 22.8763Z" fill="black"/>
  </svg>
);

// ─── Card SVG icon ────────────────────────────────────────────────────────────
const CardIcon = () => (
  <svg width="30" height="22" viewBox="0 0 24 18" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ height: 24, flexShrink: 0 }}>
    <rect x="1" y="1" width="22" height="16" rx="3" stroke="#333" strokeWidth="1.5" fill="none"/>
    <rect x="1" y="5" width="22" height="3" fill="#333"/>
    <rect x="4" y="11" width="6" height="2" rx="1" fill="#999"/>
  </svg>
);

// ─── Styled ───────────────────────────────────────────────────────────────────

// -- Type badge (SBP / Card) --
const TypeBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 10px 16px;
  margin-bottom: 16px;
  box-sizing: border-box;
  font-weight: 400;
  line-height: 1.5;
  letter-spacing: 0.00938em;
  color: black;
  font-family: 'PT Mono', monospace, sans-serif;
  font-size: 14px;
  text-align: left;
`;

// -- Requisites block (merged phone/card + amount) --
const RequisitesBlock = styled.div`
  background-color: ${LION_GREEN_LIGHT};
  border-radius: 20px;
  padding: 4px;
  margin-bottom: 16px;
`;

const CopyRowInner = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  padding: 10px 12px;
  box-sizing: border-box;

  & + & {
    border-top: 1px solid rgba(106, 170, 58, 0.15);
  }
`;

const CopyText = styled.div`
  font-weight: 400;
  line-height: 1.5;
  letter-spacing: 0.00938em;
  color: black;
  width: 100%;
  font-family: 'PT Mono', monospace, sans-serif;
  font-size: 14px;
  text-align: left;
`;

const CopyLabel = styled.div`
  font-size: 11px;
  color: ${LION_GREY};
  font-family: 'PT Mono', monospace;
`;

const CopyIconWrap = styled.button<{ $copied: boolean }>`
  background: rgba(106, 170, 58, 0.12);
  border: none;
  border-radius: 8px;
  width: 36px;
  height: 36px;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  opacity: ${({ $copied }) => ($copied ? 1 : 0.75)};
  transition: opacity 0.2s, box-shadow 0.2s, transform 0.1s;
  &:hover {
    opacity: 1;
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.18);
  }
  &:active { transform: scale(0.9); }
`;

const CopyIconSvg = () => (
  <svg width="18" height="18" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M10.6654 8.6026V11.4026C10.6654 13.7359 9.73203 14.6693 7.3987 14.6693H4.5987C2.26536 14.6693 1.33203 13.7359 1.33203 11.4026V8.6026C1.33203 6.26927 2.26536 5.33594 4.5987 5.33594H7.3987C9.73203 5.33594 10.6654 6.26927 10.6654 8.6026Z" stroke="#171717" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M14.6654 4.6026V7.4026C14.6654 9.73594 13.732 10.6693 11.3987 10.6693H10.6654V8.6026C10.6654 6.26927 9.73203 5.33594 7.3987 5.33594H5.33203V4.6026C5.33203 2.26927 6.26536 1.33594 8.5987 1.33594H11.3987C13.732 1.33594 14.6654 2.26927 14.6654 4.6026Z" stroke="#171717" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const CopyDoneSvg = () => (
  <svg width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M4 10l4 4 8-8" stroke="#6aaa3a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// -- Bank row (no background, no icon) --
const BankRow = styled.div`
  padding: 8px 14px;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  font-family: 'PT Mono', monospace;
`;

const BankLabel = styled.div`
  font-size: 11px;
  color: #aaa;
  margin-bottom: 2px;
`;

const BankName = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: #333;
`;

const BankLogoWrap = styled.div`
  margin-left: auto;
  flex-shrink: 0;
  display: flex;
  align-items: center;

  img {
    height: 24px;
    width: auto;
    display: block;
  }
`;

const InstructionPanel = styled.div`
  background: rgba(26, 125, 214, 0.04);
  border: 1px solid rgba(26, 125, 214, 0.12);
  border-radius: 12px;
  padding: 14px 16px;
  margin-bottom: 12px;
  text-align: left;
`;

// Фирменный знак m10 — такой же цветной логотип бренда, как знак СБП рядом: клиент узнаёт
// приложение, в котором будет платить, раньше, чем читает заголовок.
const M10Icon = () => (
  <img src={m10Logo} alt='m10' style={{ height: 24, width: 24, flexShrink: 0 }} />
);

/**
 * Единственная тревожная плашка на экране. Она здесь потому, что цена ошибки необратима: номер
 * выдан под один депозит, и второй перевод на него деньгами клиента уже не станет.
 */
const WarnNote = styled.p`
  margin: 0 0 12px;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid #f0b8a8;
  background: #fff4f0;
  font-family: 'PT Mono', monospace;
  font-size: 12px;
  line-height: 1.6;
  color: #b3341f;
  text-align: left;
`;

const InstructionList = styled.ol`
  margin: 0;
  padding: 0 0 0 20px;
  font-family: 'PT Mono', monospace;
  font-size: 12px;
  color: #333;
  line-height: 1.7;
`;

// -- Footer buttons --
const FooterBtnRow = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 10px;
  margin-bottom: 10px;
  @media (max-width: 575px) {
    flex-direction: column-reverse;
  }
`;

const SbpBackBtn = styled.button`
  color: rgb(13, 110, 253);
  background-color: transparent;
  border: 1.5px solid rgb(13, 110, 253);
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 5px;
  font-size: 14px;
  font-weight: 400;
  font-family: PT Sans, sans-serif;
  padding: 8px 20px;
  cursor: pointer;
  transition: all 0.2s;
  &:hover { background-color: rgba(13, 110, 253, 0.06); }
`;

const SbpPaidBtn = styled.button`
  background-color: #93c84a;
  color: #fff;
  border: 1.5px solid transparent;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 5px 20px rgba(0,0,0,0.2);
  border-radius: 5px;
  font-size: 14px;
  font-weight: 400;
  font-family: PT Sans, sans-serif;
  padding: 8px 20px;
  cursor: pointer;
  transition: all 0.2s;
  &:hover { opacity: 0.85; }
  &:disabled { cursor: not-allowed; opacity: 0.6; }
`;

// ─── Copy row inside block ───────────────────────────────────────────────────
const BlockCopyRow = ({ label, value, copyValue }: { label: string; value: string; copyValue?: string }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    copyToClipboard((copyValue ?? value).replace(/\s/g, '')).then((ok) => {
      if (!ok) return;
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };
  return (
    <CopyRowInner>
      <CopyText>
        <CopyLabel>{label}</CopyLabel>
        {value}
      </CopyText>
      <CopyIconWrap $copied={copied} onClick={handleCopy}>
        {copied ? <CopyDoneSvg /> : <CopyIconSvg />}
      </CopyIconWrap>
    </CopyRowInner>
  );
};

// ─── Component ────────────────────────────────────────────────────────────────
interface Props {
  transaction: Transaction;
  uuid: string;
  timeLeft: string;
  onCancel: (reason?: CancelReasonOption) => void;
  /** Cancel as a refusal of what this screen offers. */
  onDeclineOffer: (context: DeclineContext) => Promise<void> | void;
  /** Cancel survey items, in order; empty — no survey. */
  cancelReasons?: CancelReasonOption[];
  /** CPAYMENT-1730: уход к мерчанту без отмены заявки. */
  onLeave?: () => void;
  /** CPAYMENT-1730: клиент уже отметил «Я оплатил» — выход спрашивает, что именно он хочет. */
  receiptIntent?: boolean;
  /** CPAYMENT-1731: до конца окна осталось меньше 2 минут и его ещё можно продлить. */
  lowTimeWarning?: boolean;
  onExtendTime?: () => void;
  onDismissLowTime?: () => void;
  onConfirm: () => void;
  onShowUpload: () => void;
}

const LionSbpPage = ({ transaction, uuid, timeLeft, onCancel, onDeclineOffer, cancelReasons, onLeave, receiptIntent, lowTimeWarning, onExtendTime, onDismissLowTime, onConfirm, onShowUpload }: Props) => {
  const { t } = useTranslation();
  const { requisite } = transaction;
  const [showLeave, setShowLeave] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  // CPAYMENT-1637: у QR-реквизита нет ни карты, ни телефона — только ссылка
  // в paymentUrl, поэтому вместо строки с копированием показываем сам код.
  const isQr = requisite.type === 'qr_code' && !!requisite.paymentUrl;
  const isCard = !!requisite.cardNumber && !requisite.phone;
  // m10 — кошелёк, а не СБП: номер адресует конкретный счёт в приложении, выдан под один депозит
  // и живёт по азербайджанскому формату. Отсюда свой заголовок, свой знак, своя инструкция и
  // предупреждение, которого у остальных телефонных методов нет.
  const isM10 = transaction.paymentMethod === 'M10AZN';

  const handlePaidClick = async () => {
    if (isProcessing) return;
    if (transaction.simpleConfirmation === false) {
      onShowUpload();
      return;
    }
    setIsProcessing(true);
    try {
      await onConfirm();
    } finally {
      setIsProcessing(false);
    }
  };

  const getBankLabel = () => {
    const method = transaction.paymentMethod?.toUpperCase() ?? '';
    if (method.includes('SBER')) return 'Сбер';
    if (method.includes('ALFA')) return 'Альфа';
    if (method.includes('TBANK') || method.includes('TINKOFF')) return 'Т-Банк';
    if (method.includes('OZON')) return 'Озон';
    if (method.includes('VTB')) return 'ВТБ';
    return requisite.acquiringBank || '';
  };

  const formatAmount = () => `${transaction.amount.toLocaleString('ru-RU')} ${requisite.currency}`;

  return (
    <LionScreen
      step={1}
      uuid={uuid}
      timeLeft={timeLeft}
      footerSlot={
        <>
          <FooterBtnRow>
            <SbpBackBtn onClick={() => setShowLeave(true)}>{t('common.back')}</SbpBackBtn>
            <SbpPaidBtn onClick={handlePaidClick} disabled={isProcessing}>{t('sbp.iPaid')}</SbpPaidBtn>
          </FooterBtnRow>
          {showLeave && (
            <LionLeaveModal
              onLeave={onCancel}
              onCancel={() => setShowLeave(false)}
              reasons={cancelReasons}
              receiptIntent={receiptIntent}
              onLeaveWithoutCancel={onLeave}
            />
          )}
          {/* CPAYMENT-1731: не наслаиваем предупреждение на уже открытый диалог выхода. */}
          {lowTimeWarning && !showLeave && onExtendTime && onDismissLowTime && (
            <LionLowTimeModal onExtend={onExtendTime} onDismiss={onDismissLowTime} />
          )}
        </>
      }
    >
      {/* Type badge */}
      <TypeBadge>
        {isQr ? <QrIcon /> : isCard ? <CardIcon /> : isM10 ? <M10Icon /> : <SbpIcon />}
        <span>
          {isQr
            ? t('qr.title')
            : isCard
              ? t('sbp.transferByCard')
              : isM10
                ? t('m10.transferByPhone')
                : t('sbp.transferByPhone')}
        </span>
      </TypeBadge>

      {/* Merged requisites + amount block */}
      <RequisitesBlock>
        {isQr && (
          <QrPayBlock
            url={requisite.paymentUrl!}
            hint={t('qr.hint')}
            action={t('qr.action')}
            accent={LION_GREEN_LIGHT}
          />
        )}
        {isQr && requisite.name && (
          <BlockCopyRow label={t('qr.recipient')} value={requisite.name} />
        )}
        {!isQr && isCard && requisite.cardNumber && (
          <BlockCopyRow label={t('sbp.copyCard')} value={getCardNumberMask(requisite.cardNumber) || requisite.cardNumber} copyValue={requisite.cardNumber} />
        )}
        {!isQr && !isCard && requisite.phone && (
          <BlockCopyRow
            label={t('sbp.copyPhone')}
            value={(isM10 ? getAzPhoneNumberMask(requisite.phone) : getPhoneNumberMask(requisite.phone)) || requisite.phone}
            copyValue={requisite.phone}
          />
        )}
        <BlockCopyRow label={t('sbp.copyAmount')} value={formatAmount()} />
      </RequisitesBlock>

      {/* Bank — name only, no bg, no icon */}
      {getBankLabel() && (
        <BankRow>
          <div>
            <BankLabel>{t('sbp.selectBank')}</BankLabel>
            <BankName>{getBankLabel()}</BankName>
          </div>
          <BankLogoWrap>
            <BankLogo code={requisite.acquiringBank} />
          </BankLogoWrap>
        </BankRow>
      )}

      {/* Amount changed alert */}
      {showsAmountChangeNotice(transaction) && (
        <LionAmountAlert transaction={transaction} onDecline={() => onDeclineOffer('amount_change')} />
      )}

      {isM10 && <WarnNote>{t('m10.warning')}</WarnNote>}

      {/* CPAYMENT-1722: инструкция всегда развёрнута — без клика «Полная инструкция». */}
      <InstructionPanel>
        <InstructionList>
          {isM10 ? (
            <>
              <li>{t('m10.instructionStep1')}</li>
              <li>{t('m10.instructionStep2')}</li>
              <li>{t('m10.instructionStep3')}</li>
            </>
          ) : (
            <>
              {/* Копировать у QR нечего — первые два шага про реквизиты пропускаем. */}
              {!isQr && <li>{t('sbp.instructionStep1')}</li>}
              {!isQr && <li>{t('sbp.instructionStep2')}</li>}
              <li>{t('sbp.instructionStep3')}</li>
              <li>{t('sbp.instructionStep4')}</li>
              <li>{t('sbp.instructionStep5')}</li>
            </>
          )}
        </InstructionList>
      </InstructionPanel>
    </LionScreen>
  );
};

export default LionSbpPage;
